import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin, Result } from 'antd';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppLayout } from './components/Common/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SubscriberPage from './pages/SubscriberPage';
import MeterPage from './pages/MeterPage';
import BillingPage from './pages/BillingPage';
import ReadingPage from './pages/ReadingPage';
import PaymentPage from './pages/PaymentPage';
import ReportPage from './pages/ReportPage';
import SettingsPage from './pages/SettingsPage';
import ManageUsersPage from './pages/ManageUsersPage';
import ProfilePage from './pages/ProfilePage';
import AuditLogPage from './pages/AuditLogPage';
import './App.css';

// Component for access denied
const AccessDenied = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <Result
      status="403"
      title="Accès Refusé"
      subTitle="Vous n'avez pas les permissions nécessaires pour accéder à cette page."
      style={{ width: '100%' }}
    />
  </div>
);

// AdminRoute component - protège les routes administrateur
const AdminRoute = ({ children }) => {
  const { authenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Vérifie si l'utilisateur est admin
  if (user?.role !== 'admin') {
    return <AccessDenied />;
  }

  return children;
};

// ProtectedRoute component pour les routes authentifiées
const ProtectedRoute = ({ children }) => {
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// AppContent component pour la logique de routing
const AppContent = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { authenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppLayout collapsed={collapsed} setCollapsed={setCollapsed}>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/subscribers" element={<SubscriberPage />} />
        <Route path="/meters" element={<MeterPage />} />
        <Route path="/readings" element={<ReadingPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/payments" element={<PaymentPage />} />
        <Route path="/reports" element={<ReportPage />} />
        <Route 
          path="/settings" 
          element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          } 
        />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route 
          path="/manage-users" 
          element={
            <AdminRoute>
              <ManageUsersPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/audit-logs" 
          element={
            <AdminRoute>
              <AuditLogPage />
            </AdminRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
